// Changing a booking form after it has left DRAFT (Leon, 2026-10-14) — the words both
// form detail screens (phone + desktop) use, kept in one place so they cannot drift.
//
// Out for signature, not issued → "Edit": back to a DRAFT under the same ref, every
// signature and link cancelled. Issued → "Revise": a copy with its own ref that
// replaces the issued form only when it is issued itself.

export const REOPEN_POINTS = [
  'It goes back to a draft with the same number, so you can change anything.',
  'Signatures already taken and signing links already sent are cancelled.',
  'Everyone signs again once you have made the change.',
];

// A revision is a new form, so its owner's Letter of Undertaking gets a new LOU number
// too (an Edit keeps both numbers) — said plainly, since the old LOU number goes with
// the old form.
export const revisePoints = (form) => [
  form?.lou_included
    ? 'A new form is started as a copy of this one, with its own number — and its Letter of Undertaking gets its own new LOU number too.'
    : 'A new form is started as a copy of this one, with its own number.',
  `${form?.form_ref || 'This form'} stays in force until you issue the new one.`,
  form?.listing_id
    ? `When you issue it, ${form?.form_ref || 'this form'} is cancelled as revised and the listing stays under offer.`
    : `When you issue it, ${form?.form_ref || 'this form'} is cancelled as revised.`,
];

/** The revision trail as rows for the "Linked" list: what this revises / replaced it / is open on it. */
export const revisionRows = (form) => [
  form?.revises && {
    k: 'revises', icon: '↺', ref: form.revises.form_ref,
    sub: form.revises.status_raw === 'APPROVED'
      ? 'Revised by this form — cancelled when this one is issued'
      : 'The form this one revised',
    chip: form.revises.status,
    to: `/forms/${form.revises.id}`,
  },
  form?.open_revision && {
    k: 'open-revision', icon: '↻', ref: form.open_revision.form_ref,
    sub: 'Revision in progress — replaces this form when issued',
    chip: form.open_revision.status,
    tone: 'warn',
    to: `/forms/${form.open_revision.id}`,
  },
  // Kept even once that revision was itself revised or cancelled: its chip then says
  // Cancelled, not a green "in force", and its own page carries the trail onward.
  form?.revised_by && {
    k: 'revised-by', icon: '↻', ref: form.revised_by.form_ref,
    sub: 'Replaced this form',
    chip: form.revised_by.status,
    tone: form.revised_by.status_raw === 'CANCELLED' ? undefined : 'ok',
    to: `/forms/${form.revised_by.id}`,
  },
].filter(Boolean);

/**
 * Why an issued OTP/OTR offers no "Convert to sale": a revision is open on it, and the
 * sale must be made from whichever form ends up in force (the server refuses it too).
 * Null when that is not the reason.
 */
export const convertBlockedByRevision = (form) => (
  form?.open_revision
  && form.status_raw === 'APPROVED'
  && !form.sale_id
  && ['OTP', 'OTR'].includes(form.form_type)
    ? `Revision ${form.open_revision.form_ref} is open. Issue or cancel it first, then create the sale.`
    : null
);

/** The edit screen opened on a form that is no longer a draft: where to go instead. */
export const lockedEditHint = (statusRaw) => {
  if (['PENDING_SIGNING', 'SIGNED', 'PENDING_APPROVAL'].includes(statusRaw)) {
    return 'Tap Edit at the top of the form page to make it a draft again (signatures are cleared).';
  }
  if (statusRaw === 'APPROVED') return 'Tap Revise at the top of the form page to change it.';
  return null;
};
